import { useState, useRef, useEffect } from 'react';
import { AlertTriangle } from 'lucide-react';
import { Modal } from '@/components/ui/Modal';
import { Input } from '@/components/ui/Input';
import { Button } from '@/components/ui/Button';
import { useT } from '@/store/i18n';

interface Props {
  open: boolean;
  title: string;
  description: React.ReactNode;
  warningItems?: string[];
  onClose: () => void;
  onConfirm: (password: string) => Promise<void>;
}

export function PasswordConfirmModal({
  open,
  title,
  description,
  warningItems,
  onClose,
  onConfirm,
}: Props) {
  const t = useT();
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open) {
      setPassword('');
      setError('');
      setLoading(false);
      setTimeout(() => inputRef.current?.focus(), 60);
    }
  }, [open]);

  async function handleConfirm() {
    if (!password) return;
    setError('');
    setLoading(true);
    try {
      await onConfirm(password);
    } catch (e) {
      const msg = e instanceof Error ? e.message : t('delete.fail');
      setError(msg.includes('รหัสผ่าน') || msg.toLowerCase().includes('password') ? msg : t('pwd.wrong'));
      setLoading(false);
    }
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'Enter' && password && !loading) handleConfirm();
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={
        <span className="flex items-center gap-2 text-red-600">
          <AlertTriangle className="w-5 h-5 flex-shrink-0" />
          {title}
        </span>
      }
      size="md"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={loading}>
            {t('btn.cancel')}
          </Button>
          <Button
            variant="danger"
            onClick={handleConfirm}
            disabled={!password || loading}
            loading={loading}
          >
            {t('delete.btn_confirm')}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <p className="text-sm text-slate-600">{description}</p>

        {warningItems && warningItems.length > 0 && (
          <div className="max-h-36 overflow-y-auto rounded-lg border border-red-100 bg-red-50 px-3 py-2 space-y-1">
            {warningItems.map((item, i) => (
              <p key={i} className="text-xs text-red-700 truncate">
                • {item}
              </p>
            ))}
          </div>
        )}

        <Input
          ref={inputRef}
          type="password"
          label={t('pwd.label')}
          placeholder={t('pwd.placeholder')}
          value={password}
          onChange={(e) => { setPassword(e.target.value); setError(''); }}
          onKeyDown={handleKeyDown}
          error={error}
          disabled={loading}
          autoComplete="current-password"
        />
      </div>
    </Modal>
  );
}
